import React, { useState, useEffect, useCallback } from "react";

// ── Types ──────────────────────────────────────────────────────────────────────

interface Lead {
  name: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  category?: string;
  source_url: string;
  score?: number;
  score_label?: "hot" | "warm" | "cold";
  notes?: string;
}

interface Job {
  job_id: string;
  target: string;
  status: "queued" | "running" | "done" | "failed";
  lead_count: number;
  started_at: string;
  finished_at?: string;
  finalized?: boolean;
  crm_lead_count?: number;
  leads?: Lead[];
}

interface SchedulerJob {
  id: string;
  name: string;
  next_run: string | null;
  next_run_human: string;
  timezone: string;
}

interface SchedulerStatus {
  running: boolean;
  jobs: SchedulerJob[];
  outreach_sent_today: number;
  outreach_daily_limit: number;
  leads_discovered_today: number;
}

const SCORE_COLOR: Record<string, string> = {
  hot:  "#ff6b6b",
  warm: "#ffd6a5",
  cold: "#7ec8e3",
};

const SCORE_EMOJI: Record<string, string> = {
  hot: "🔥",
  warm: "🌡️",
  cold: "❄️",
};

const STATUS_COLOR: Record<string, string> = {
  queued:  "#7ec8e3",
  running: "#ffd6a5",
  done:    "#b8d58d",
  failed:  "#ff6b6b",
};

// ── Component ─────────────────────────────────────────────────────────────────

const MarketIntelligence: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [crawlerStatus, setCrawlerStatus] = useState<"online" | "offline" | "checking">("checking");
  const [scheduler, setScheduler] = useState<SchedulerStatus | null>(null);
  const [triggering, setTriggering] = useState<string | null>(null);
  const [triggerMsg, setTriggerMsg] = useState<string>("");

  // New crawl form
  const [formTarget, setFormTarget] = useState("yellow_pages_ae");
  const [formKeywords, setFormKeywords] = useState("beauty salon, hair salon, spa");
  const [formMaxLeads, setFormMaxLeads] = useState(50);
  const [formSeedUrls, setFormSeedUrls] = useState("");
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState("");

  // Finalize state
  const [finalizeMsg, setFinalizeMsg] = useState("");
  const [finalizing, setFinalizing] = useState<string | null>(null);
  const [finalizeResult, setFinalizeResult] = useState<Record<string, string>>({});

  // ── Fetch jobs ──────────────────────────────────────────────────────────────
  const fetchJobs = useCallback(async () => {
    try {
      const r = await fetch("/api/market-intel/jobs");
      if (r.ok) setJobs(await r.json());
    } catch {}
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await fetch("/api/market-intel/status");
      if (r.ok) {
        const d = await r.json();
        setCrawlerStatus(d.crawler_service === "online" ? "online" : "offline");
        if (d.scheduler) setScheduler(d.scheduler as SchedulerStatus);
      }
    } catch {
      setCrawlerStatus("offline");
    }
  }, []);

  const triggerJob = async (jobId: string, label: string) => {
    setTriggering(jobId);
    setTriggerMsg("");
    try {
      const endpoint =
        jobId === "morning_report"
          ? "/api/market-intel/report/morning/trigger"
          : `/api/market-intel/scheduler/trigger/${jobId}`;
      const r = await fetch(endpoint, { method: "POST" });
      const data = await r.json();
      setTriggerMsg(r.ok ? `✅ ${label} fired` : `❌ ${data.detail || "failed"}`);
      await fetchStatus();
    } catch (e: any) {
      setTriggerMsg(`❌ ${e.message}`);
    } finally {
      setTriggering(null);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchJobs();
    const interval = setInterval(() => {
      fetchStatus();
      fetchJobs();
    }, 8000);
    return () => clearInterval(interval);
  }, [fetchStatus, fetchJobs]);

  // ── Auto-poll running jobs ──────────────────────────────────────────────────
  useEffect(() => {
    const running = jobs.some((j) => j.status === "queued" || j.status === "running");
    if (running) {
      const t = setTimeout(fetchJobs, 3000);
      return () => clearTimeout(t);
    }
  }, [jobs, fetchJobs]);

  // ── Refresh selected job detail ────────────────────────────────────────────
  const loadJobDetail = async (jobId: string) => {
    try {
      const r = await fetch(`/api/market-intel/jobs/${jobId}`);
      if (r.ok) {
        const data = await r.json();
        setSelectedJob(data);
      }
    } catch {}
  };

  // ── Start crawl ─────────────────────────────────────────────────────────────
  const handleStartCrawl = async () => {
    setStarting(true);
    setStartError("");
    try {
      const body: any = {
        target: formTarget,
        keywords: formKeywords.split(",").map((k) => k.trim()).filter(Boolean),
        max_leads: formMaxLeads,
        seed_urls: formTarget === "custom"
          ? formSeedUrls.split("\n").map((u) => u.trim()).filter(Boolean)
          : [],
      };
      const r = await fetch("/api/market-intel/crawl", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json();
        setStartError(err.detail || "Failed to start crawl");
      } else {
        await fetchJobs();
      }
    } catch (e: any) {
      setStartError(e.message);
    } finally {
      setStarting(false);
    }
  };

  // ── Finalize job ────────────────────────────────────────────────────────────
  const handleFinalize = async (jobId: string) => {
    setFinalizing(jobId);
    try {
      const r = await fetch(`/api/market-intel/jobs/${jobId}/finalize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: finalizeMsg || undefined }),
      });
      const data = await r.json();
      if (r.ok) {
        setFinalizeResult((prev) => ({ ...prev, [jobId]: `✅ ${data.message}` }));
        await fetchJobs();
        if (selectedJob?.job_id === jobId) await loadJobDetail(jobId);
      } else {
        setFinalizeResult((prev) => ({ ...prev, [jobId]: `❌ ${data.detail}` }));
      }
    } catch (e: any) {
      setFinalizeResult((prev) => ({ ...prev, [jobId]: `❌ ${e.message}` }));
    } finally {
      setFinalizing(null);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: "24px 32px", fontFamily: "'Space Grotesk', sans-serif", color: "#dae2fd" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontFamily: "'Manrope', sans-serif", fontWeight: 800, margin: 0, color: "#ffd6a5" }}>
            🕷️ Market Intelligence
          </h1>
          <div style={{ fontSize: 13, color: "#4a5568", marginTop: 4 }}>
            Crawl UAE business directories → Score leads → Push to CRM → Notify team
          </div>
        </div>
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          background: "rgba(19,27,46,0.6)",
          border: `1px solid ${crawlerStatus === "online" ? "rgba(184,213,141,0.3)" : "rgba(255,107,107,0.3)"}`,
          borderRadius: 10, padding: "6px 14px", fontSize: 12,
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: "50%",
            background: crawlerStatus === "online" ? "#b8d58d" : crawlerStatus === "offline" ? "#ff6b6b" : "#ffd6a5",
            display: "inline-block",
            boxShadow: crawlerStatus === "online" ? "0 0 8px #b8d58d" : undefined,
          }} />
          Crawler: {crawlerStatus}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: 24 }}>

        {/* Left panel: scheduler status + new crawl form + jobs list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Scheduler status + manual triggers */}
          <div style={{
            background: "rgba(19,27,46,0.7)",
            border: "1px solid rgba(189,178,255,0.25)",
            borderRadius: 14, padding: 20,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#bdb2ff" }}>
                ⏰ Scheduler
              </div>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99,
                background: scheduler?.running ? "rgba(184,213,141,0.15)" : "rgba(255,107,107,0.15)",
                color: scheduler?.running ? "#b8d58d" : "#ff6b6b",
                border: `1px solid ${scheduler?.running ? "#b8d58d55" : "#ff6b6b55"}`,
              }}>
                {scheduler?.running ? "● RUNNING" : "○ STOPPED"}
              </span>
            </div>

            {/* Counters */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
              <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: "8px 10px" }}>
                <div style={{ fontSize: 10, color: "#7a8aad", textTransform: "uppercase" }}>Leads today</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#b8d58d" }}>
                  {scheduler?.leads_discovered_today ?? 0}
                </div>
              </div>
              <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: "8px 10px" }}>
                <div style={{ fontSize: 10, color: "#7a8aad", textTransform: "uppercase" }}>Outreach sent</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#ffd6a5" }}>
                  {scheduler?.outreach_sent_today ?? 0}
                  <span style={{ fontSize: 11, color: "#7a8aad", fontWeight: 600 }}>
                    /{scheduler?.outreach_daily_limit ?? 30}
                  </span>
                </div>
              </div>
            </div>

            {/* Next runs */}
            {scheduler?.jobs && scheduler.jobs.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                {scheduler.jobs.map((j) => (
                  <div key={j.id} style={{
                    fontSize: 11, color: "#7a8aad",
                    display: "flex", justifyContent: "space-between",
                    padding: "4px 0", borderBottom: "1px solid rgba(189,178,255,0.08)",
                  }}>
                    <span style={{ color: "#dae2fd" }}>{j.name}</span>
                    <span>{j.next_run_human}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Manual triggers */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {[
                { id: "morning_report", label: "📊 Morning Report" },
                { id: "market_intel_crawl", label: "🕷️ Crawl Now" },
                { id: "market_intel_finalize", label: "✅ Finalize → CRM" },
                { id: "market_intel_outreach", label: "💬 Outreach Now" },
              ].map((btn) => (
                <button
                  key={btn.id}
                  onClick={() => triggerJob(btn.id, btn.label)}
                  disabled={triggering === btn.id}
                  style={{
                    padding: "7px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                    background: triggering === btn.id ? "rgba(189,178,255,0.1)" : "rgba(189,178,255,0.1)",
                    color: "#bdb2ff",
                    border: "1px solid rgba(189,178,255,0.3)",
                    cursor: triggering === btn.id ? "not-allowed" : "pointer",
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}
                >
                  {triggering === btn.id ? "..." : btn.label}
                </button>
              ))}
            </div>
            {triggerMsg && (
              <div style={{
                marginTop: 10, fontSize: 11,
                color: triggerMsg.startsWith("✅") ? "#b8d58d" : "#ff6b6b",
              }}>
                {triggerMsg}
              </div>
            )}
          </div>

          {/* New crawl form */}
          <div style={{
            background: "rgba(19,27,46,0.7)",
            border: "1px solid rgba(78,69,59,0.25)",
            borderRadius: 14, padding: 20,
          }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: "#ffd6a5" }}>
              🚀 New Crawl
            </div>

            <Label>Target Source</Label>
            <select
              value={formTarget}
              onChange={(e) => setFormTarget(e.target.value)}
              style={selectStyle}
            >
              <option value="yellow_pages_ae">Yellow Pages AE</option>
              <option value="uae_beauty_dir">UAE Beauty Directory</option>
              <option value="both">Both (default)</option>
              <option value="custom">Custom URLs</option>
            </select>

            <Label>Keywords (comma-separated)</Label>
            <input
              type="text"
              value={formKeywords}
              onChange={(e) => setFormKeywords(e.target.value)}
              placeholder="beauty salon, hair, spa..."
              style={inputStyle}
            />

            <Label>Max Leads</Label>
            <input
              type="number"
              value={formMaxLeads}
              onChange={(e) => setFormMaxLeads(Number(e.target.value))}
              min={5} max={500}
              style={inputStyle}
            />

            {formTarget === "custom" && (
              <>
                <Label>Seed URLs (one per line)</Label>
                <textarea
                  value={formSeedUrls}
                  onChange={(e) => setFormSeedUrls(e.target.value)}
                  rows={3}
                  placeholder="https://..."
                  style={{ ...inputStyle, resize: "vertical" }}
                />
              </>
            )}

            {startError && (
              <div style={{ color: "#ff6b6b", fontSize: 12, marginBottom: 10 }}>{startError}</div>
            )}

            <button
              onClick={handleStartCrawl}
              disabled={starting || crawlerStatus !== "online"}
              style={{
                width: "100%", padding: "10px 0", marginTop: 4,
                background: starting ? "rgba(255,214,165,0.15)" : "linear-gradient(135deg, #ffd6a5, #e7c090)",
                color: "#0b1326", border: "none", borderRadius: 8,
                fontWeight: 700, fontSize: 13, cursor: starting ? "not-allowed" : "pointer",
                fontFamily: "'Space Grotesk', sans-serif",
              }}
            >
              {starting ? "Starting..." : "Start Crawl"}
            </button>
          </div>

          {/* Jobs list */}
          <div style={{
            background: "rgba(19,27,46,0.7)",
            border: "1px solid rgba(78,69,59,0.25)",
            borderRadius: 14, padding: 20,
          }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, color: "#ffd6a5" }}>
              📋 Jobs ({jobs.length})
            </div>
            {jobs.length === 0 && (
              <div style={{ color: "#4a5568", fontSize: 13 }}>No crawl jobs yet.</div>
            )}
            {jobs.map((job) => (
              <div
                key={job.job_id}
                onClick={() => loadJobDetail(job.job_id)}
                style={{
                  padding: "10px 14px", borderRadius: 10, marginBottom: 8, cursor: "pointer",
                  background: selectedJob?.job_id === job.job_id
                    ? "rgba(255,214,165,0.1)"
                    : "rgba(255,255,255,0.03)",
                  border: `1px solid ${selectedJob?.job_id === job.job_id ? "rgba(255,214,165,0.3)" : "rgba(78,69,59,0.2)"}`,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#dae2fd" }}>
                    {job.target.replace(/_/g, " ")}
                  </span>
                  <StatusBadge status={job.status} />
                </div>
                <div style={{ fontSize: 11, color: "#4a5568", marginTop: 4 }}>
                  {job.lead_count} leads · {new Date(job.started_at).toLocaleString("en-AE", { timeZone: "Asia/Dubai" })}
                  {job.finalized && <span style={{ color: "#b8d58d", marginLeft: 6 }}>✅ CRM</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right panel: job detail + leads table */}
        <div>
          {!selectedJob ? (
            <div style={{
              background: "rgba(19,27,46,0.5)",
              border: "1px solid rgba(78,69,59,0.2)",
              borderRadius: 14, padding: 40,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#4a5568", fontSize: 14,
            }}>
              Select a job to view leads
            </div>
          ) : (
            <div style={{
              background: "rgba(19,27,46,0.7)",
              border: "1px solid rgba(78,69,59,0.25)",
              borderRadius: 14, padding: 24,
            }}>
              {/* Job header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#ffd6a5", fontFamily: "'Manrope', sans-serif" }}>
                    {selectedJob.target.replace(/_/g, " ")}
                  </div>
                  <div style={{ fontSize: 12, color: "#4a5568", marginTop: 3 }}>
                    Job {selectedJob.job_id?.slice(0, 8)} · {selectedJob.lead_count} leads
                    {selectedJob.finished_at && ` · Finished ${new Date(selectedJob.finished_at).toLocaleString("en-AE", { timeZone: "Asia/Dubai" })}`}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <StatusBadge status={selectedJob.status} />
                  {selectedJob.status === "done" && !selectedJob.finalized && (
                    <button
                      onClick={() => handleFinalize(selectedJob.job_id)}
                      disabled={finalizing === selectedJob.job_id}
                      style={{
                        background: "linear-gradient(135deg, #b8d58d, #8fbf5a)",
                        color: "#0b1326", border: "none", borderRadius: 8,
                        padding: "7px 16px", fontWeight: 700, fontSize: 12,
                        cursor: "pointer", fontFamily: "'Space Grotesk', sans-serif",
                      }}
                    >
                      {finalizing === selectedJob.job_id ? "Finalizing..." : "✅ Finalize → CRM + Group"}
                    </button>
                  )}
                  {selectedJob.finalized && (
                    <span style={{ color: "#b8d58d", fontSize: 12, fontWeight: 600 }}>
                      ✅ {selectedJob.crm_lead_count} leads in CRM
                    </span>
                  )}
                </div>
              </div>

              {/* Finalize message input */}
              {selectedJob.status === "done" && !selectedJob.finalized && (
                <div style={{ marginBottom: 16 }}>
                  <input
                    type="text"
                    placeholder="Optional note for group message..."
                    value={finalizeMsg}
                    onChange={(e) => setFinalizeMsg(e.target.value)}
                    style={{ ...inputStyle, width: "100%" }}
                  />
                </div>
              )}

              {/* Finalize result */}
              {finalizeResult[selectedJob.job_id] && (
                <div style={{ marginBottom: 14, fontSize: 13, color: finalizeResult[selectedJob.job_id].startsWith("✅") ? "#b8d58d" : "#ff6b6b" }}>
                  {finalizeResult[selectedJob.job_id]}
                </div>
              )}

              {/* Score summary */}
              {selectedJob.leads && selectedJob.leads.length > 0 && (
                <ScoreSummary leads={selectedJob.leads} />
              )}

              {/* Leads table */}
              {selectedJob.status === "running" || selectedJob.status === "queued" ? (
                <div style={{ textAlign: "center", padding: 40, color: "#ffd6a5", fontSize: 14 }}>
                  <div style={{ marginBottom: 12, fontSize: 24 }}>🕷️</div>
                  Crawling in progress...
                </div>
              ) : selectedJob.leads && selectedJob.leads.length > 0 ? (
                <LeadsTable leads={selectedJob.leads} />
              ) : (
                <div style={{ color: "#4a5568", fontSize: 13, textAlign: "center", padding: 20 }}>
                  {selectedJob.status === "failed" ? "Job failed — check crawler logs." : "No leads extracted."}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────────

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 600, color: "#7a8aad", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5, marginTop: 12 }}>
    {children}
  </div>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span style={{
    background: `${STATUS_COLOR[status] || "#4a5568"}22`,
    color: STATUS_COLOR[status] || "#4a5568",
    border: `1px solid ${STATUS_COLOR[status] || "#4a5568"}44`,
    borderRadius: 99, padding: "2px 9px", fontSize: 11, fontWeight: 700,
  }}>
    {status}
  </span>
);

const ScoreSummary: React.FC<{ leads: Lead[] }> = ({ leads }) => {
  const hot = leads.filter((l) => l.score_label === "hot").length;
  const warm = leads.filter((l) => l.score_label === "warm").length;
  const cold = leads.filter((l) => l.score_label === "cold").length;
  return (
    <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
      {[["🔥 Hot", hot, "#ff6b6b"], ["🌡️ Warm", warm, "#ffd6a5"], ["❄️ Cold", cold, "#7ec8e3"]].map(([label, count, color]) => (
        <div key={label as string} style={{
          flex: 1, textAlign: "center", padding: "10px 0",
          background: `${color}15`, border: `1px solid ${color}33`, borderRadius: 10,
        }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: color as string }}>{count as number}</div>
          <div style={{ fontSize: 11, color: "#7a8aad" }}>{label as string}</div>
        </div>
      ))}
    </div>
  );
};

const LeadsTable: React.FC<{ leads: Lead[] }> = ({ leads }) => (
  <div style={{ overflowX: "auto" }}>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
      <thead>
        <tr style={{ borderBottom: "1px solid rgba(78,69,59,0.3)" }}>
          {["Score", "Name", "Phone", "Email", "Category", "Notes"].map((h) => (
            <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: "#7a8aad", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {leads.map((lead, i) => (
          <tr key={i} style={{ borderBottom: "1px solid rgba(78,69,59,0.15)" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.03)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <td style={{ padding: "8px 10px" }}>
              <span style={{
                background: `${SCORE_COLOR[lead.score_label || "warm"]}22`,
                color: SCORE_COLOR[lead.score_label || "warm"],
                borderRadius: 99, padding: "2px 8px", fontWeight: 700,
              }}>
                {SCORE_EMOJI[lead.score_label || "warm"]} {lead.score ?? "—"}
              </span>
            </td>
            <td style={{ padding: "8px 10px", color: "#dae2fd", fontWeight: 600, maxWidth: 180 }}>
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lead.name}</div>
              {lead.website && (
                <a href={lead.website} target="_blank" rel="noreferrer"
                  style={{ color: "#7a8aad", fontSize: 10, textDecoration: "none" }}>
                  🔗 {new URL(lead.website.startsWith("http") ? lead.website : `https://${lead.website}`).hostname}
                </a>
              )}
            </td>
            <td style={{ padding: "8px 10px", color: "#b8d58d" }}>{lead.phone || "—"}</td>
            <td style={{ padding: "8px 10px", color: "#c8bfff", maxWidth: 160 }}>
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lead.email || "—"}</div>
            </td>
            <td style={{ padding: "8px 10px", color: "#7a8aad" }}>{lead.category || "—"}</td>
            <td style={{ padding: "8px 10px", color: "#4a5568", maxWidth: 200 }}>
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={lead.notes}>
                {lead.notes || "—"}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── Shared styles ──────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: "100%", boxSizing: "border-box",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(78,69,59,0.3)",
  borderRadius: 8, padding: "8px 12px",
  color: "#dae2fd", fontSize: 13,
  fontFamily: "'Space Grotesk', sans-serif",
  outline: "none",
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  cursor: "pointer",
};

export default MarketIntelligence;
